import { REPORT_LANGS, ReportLang } from "../../i18n/reportLang";
import classes from "./ReportLangPicker.module.css";

/**
 * Each language named in itself, never translated.
 *
 * A reader who cannot read the language currently on screen still has to find
 * their own — "Russian" is useless to someone who only reads Russian, "Русский"
 * is not. This is why every language switcher on the web works this way.
 */
const LANG_NAMES: Record<ReportLang, string> = {
  en: "English",
  ru: "Русский",
  zh: "中文",
};

type Props = {
  value: ReportLang;
  onChange: (lang: ReportLang) => void;
  /** Accessible name for the group, localized by the caller. */
  label: string;
};

/**
 * Language switcher for the report, rendered as page chrome.
 *
 * Deliberately outside `#content`, which is the element `GeneratePDF` hands to
 * jsPDF — anything inside it would be printed into the client's PDF. It floats
 * on the left rail, opposite that button.
 *
 * Buttons rather than a `<select>`: three fixed options fit on one row, and the
 * current language stays visible instead of being hidden behind a closed
 * dropdown. `aria-pressed` (not `aria-selected`, which belongs to tabs and
 * listbox options) is what conveys the state to a screen reader.
 */
const ReportLangPicker = ({ value, onChange, label }: Props): JSX.Element => (
  <div className={classes.container} role="group" aria-label={label}>
    {REPORT_LANGS.map((code) => {
      const isActive = code === value;
      return (
        <button
          key={code}
          type="button"
          // `lang` makes a screen reader pronounce each name in its own
          // language, and lets the browser pick a font that can render it.
          lang={code}
          className={`${classes.option} ${isActive ? classes.active : ""}`}
          aria-pressed={isActive}
          onClick={() => !isActive && onChange(code)}
        >
          {LANG_NAMES[code]}
        </button>
      );
    })}
  </div>
);

export default ReportLangPicker;
